import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Dropdown, DropdownMenuItem, Stack, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import {getCreatedMessage, supportedParticipantKinds} from '../../communicationUtils';

import backend from 'utils/backend';

const InternalComposer = (props) => {
  const {
    onMessageCreated,
    selectedParticipantKind,
    selectedParticipantResourceRn,
    user,
  } = props;
  const [message, setMessage] = React.useState('');
  const [internalMessageType, setInternalMessageType] = React.useState('COMMENTS');
  const [isSending, setIsSending] = React.useState(false);
  const [sendFailed, setSendFailed] = React.useState(false);

  const isSupportedParticipant = supportedParticipantKinds.includes(selectedParticipantKind);
  const isSubmitDisabled = isSending || !message.trim();

  React.useEffect(() => {
    setMessage('');
    setInternalMessageType('COMMENTS');
    setSendFailed(false);
  }, [selectedParticipantResourceRn]);

  const createInternalNote = async () => {
    const body = {
      body: {
        blob: message,
        editorType: 'MARKDOWN',
      },
      messageType: internalMessageType,
      requester: {
        rn: user.userRn,
      },
    };

    if (selectedParticipantKind === 'CLIENT') {
      body.source = 'WEBSITE';
      body.sentBy = 'ADMIN';
    }

    const output = selectedParticipantKind === 'CLIENT' ?
      await backend.client.message.create(selectedParticipantResourceRn, body) :
      await backend.caregiver.message.create(selectedParticipantResourceRn, body);

    return getCreatedMessage(output);
  };

  const onSubmit = async () => {
    if (isSubmitDisabled || !selectedParticipantResourceRn) return;

    setIsSending(true);
    setSendFailed(false);

    try {
      const createdMessage = await createInternalNote();

      if (createdMessage) {
        onMessageCreated(createdMessage);
      }
      setMessage('');
      setInternalMessageType('COMMENTS');
    } catch {
      setSendFailed(true);
    } finally {
      setIsSending(false);
    }
  };

  if (!isSupportedParticipant) {
    return (
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          color: '#6D7280',
        }}
      >
        <Translate expression='case.Communication.Compose.UnsupportedMethod' />
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid #ECECF1',
        borderRadius: '9px',
        backgroundColor: '#FFFFFF',
        p: '14px 15px',
      }}
    >
      <Stack sx={{gap: 1}}>
        <Box
          sx={{
            '& .MuiInputLabel-root': {
              fontSize: '11px',
              color: '#9A9BAE',
              transform: 'translate(0, -8px) scale(1)',
              position: 'relative',
              mb: '4px',
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(0, -8px) scale(1)',
            },
            '& .MuiOutlinedInput-root': {
              height: '36px',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              fontSize: '13px',
              color: '#050A14',
            },
            '& .MuiSelect-select': {
              py: '8px',
              px: '12px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#DCDDE5',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1D3DD',
            },
            '& legend': {
              display: 'none',
            },
          }}
        >
          <Dropdown
            label={translate('case.Communication.Compose.InternalTypeLabel')}
            onChange={setInternalMessageType}
            value={internalMessageType}
          >
            <DropdownMenuItem value='COMMENTS'>
              {translate('case.Communication.Compose.InternalTypes.Comments')}
            </DropdownMenuItem>
            <DropdownMenuItem value='ANNOUNCEMENTS'>
              {translate('case.Communication.Compose.InternalTypes.Announcements')}
            </DropdownMenuItem>
          </Dropdown>
        </Box>
        <TextField
          fullWidth
          minRows={3}
          multiline
          onChange={setMessage}
          placeholder={translate('case.Communication.Compose.InternalPlaceholder')}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '13px',
              color: '#050A14',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#DCDDE5',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1D3DD',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#7F8493',
              opacity: 1,
            },
          }}
          value={message}
        />
        {
          sendFailed && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#D85D31',
              }}
            >
              <Translate expression='case.Communication.Compose.SendFailed' />
            </Typography>
          )
        }
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            disabled={isSubmitDisabled}
            loading={isSending}
            onClick={onSubmit}
            sx={{
              'height': '32px',
              'borderRadius': '7px',
              'backgroundColor': '#E07955',
              'color': '#FFFFFF',
              'textTransform': 'none',
              'boxShadow': 'none',
              '&:hover': {
                backgroundColor: '#D96B48',
                boxShadow: 'none',
              },
            }}
          >
            <Translate expression='case.Communication.Compose.SaveNote' />
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

InternalComposer.propTypes = {
  onMessageCreated: PropTypes.func.isRequired,
  selectedParticipantKind: PropTypes.string,
  selectedParticipantResourceRn: PropTypes.string,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(InternalComposer));
